import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Search, Truck, Package, MapPin, Loader2, XCircle, CheckCircle2, Clock } from "lucide-react";

const STEPS = [
  { key: "preparing", label: "Preparing", Icon: Package, hint: "Your printed deed is being hand-finished by our team." },
  { key: "dispatched", label: "Dispatched", Icon: Truck, hint: "Your parcel is on the way with the courier." },
  { key: "delivered", label: "Delivered", Icon: CheckCircle2, hint: "Your commemorative deed has been delivered." },
];

export default function Track() {
  const [sp] = useSearchParams();
  const [rn, setRn] = useState(sp.get("rn") || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const doTrack = async (num) => {
    if (!num) return;
    setBusy(true);
    setResult(null);
    setNotFound(false);
    try {
      const { data } = await api.get(`/verify/${num.trim()}`);
      setResult(data);
    } catch {
      setNotFound(true);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (sp.get("rn")) doTrack(sp.get("rn"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentIndex = result
    ? STEPS.findIndex((s) => s.key === result.courier_status)
    : -1;

  return (
    <div className="pt-28 pb-20 px-6 lg:px-10 relative z-10">
      <div className="max-w-3xl mx-auto text-center">
        <div className="overline text-gold">Courier Tracking</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">
          Track your Certificate
        </h1>
        <p className="text-white/60 mt-3 max-w-xl mx-auto">
          Enter your Teuralink Plot Registration Number to see live courier status,
          partner, and tracking ID.
        </p>

        <form
          className="mt-10 glass rounded-2xl p-3 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            doTrack(rn);
          }}
        >
          <Search className="w-4 h-4 ml-3 text-white/50" />
          <input
            value={rn}
            onChange={(e) => setRn(e.target.value)}
            placeholder="TLK-M-2026-123456"
            data-testid="track-input"
            className="flex-1 bg-transparent px-2 py-3 text-white placeholder:text-white/30 focus:outline-none font-mono-space uppercase"
          />
          <button className="btn-primary" type="submit" data-testid="track-btn" disabled={busy}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Track
          </button>
        </form>

        {notFound && (
          <div className="mt-8 glass rounded-2xl p-6 text-white/70 flex items-center gap-3 justify-center" data-testid="track-notfound">
            <XCircle className="w-5 h-5 text-red-400" /> No shipment found for that registration number.
          </div>
        )}

        {result && (
          <div className="mt-10 text-left" data-testid="track-result">
            {/* Header card */}
            <div className="glass rounded-3xl p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="overline text-gold">Registration No.</div>
                  <div className="font-mono-space text-2xl text-gold mt-2" data-testid="track-reg-number">
                    {result.registration_number}
                  </div>
                </div>
                <div className="text-right">
                  <div className="overline">Status</div>
                  <div className="mt-2 font-mono-space uppercase tracking-widest text-gold">
                    {result.courier_status || "preparing"}
                  </div>
                </div>
              </div>

              <div className="mt-8 grid sm:grid-cols-2 gap-6 border-t border-white/10 pt-6">
                <Item k="Registered To" v={result.customer_name} />
                <Item k="Planet" v={result.planet === "moon" ? "The Moon" : "Mars"} />
                <Item k="Plot Location" v={result.marker_label} />
                <Item k="Plot Size" v={`${result.acres} ${result.acres === 1 ? "Acre" : "Acres"}`} />
                <Item k="Purchase Date" v={new Date(result.purchase_date).toLocaleDateString()} />
                <Item k="Coordinates" v={`LAT ${result.lat?.toFixed(2)}° · LON ${result.lon?.toFixed(2)}°`} mono />
              </div>
            </div>

            {/* Shipment card */}
            <div className="glass rounded-3xl p-8 mt-4">
              <div className="overline text-gold mb-4">Shipment</div>

              <div className="grid sm:grid-cols-2 gap-6">
                <Row Icon={Truck} k="Courier Partner" v={result.courier_company || <span className="text-white/40">Awaiting dispatch</span>} testid="track-courier" />
                <Row Icon={MapPin} k="Tracking ID" v={result.tracking_number ? <span className="font-mono-space">{result.tracking_number}</span> : <span className="text-white/40">Not assigned yet</span>} testid="track-tracking" />
              </div>

              {/* Progress steps */}
              <div className="mt-10">
                <div className="relative">
                  <div className="absolute top-5 left-5 right-5 h-[2px] bg-white/10" />
                  <div
                    className="absolute top-5 left-5 h-[2px] bg-[#D4AF37] transition-all duration-700"
                    style={{ width: currentIndex >= 0 ? `calc(${(currentIndex / (STEPS.length - 1)) * 100}% - ${currentIndex === 0 ? 0 : 40}px)` : "0%" }}
                  />
                  <div className="relative grid grid-cols-3 gap-2">
                    {STEPS.map((s, i) => {
                      const active = i <= currentIndex;
                      const isCurrent = i === currentIndex;
                      return (
                        <div key={s.key} className="flex flex-col items-center text-center">
                          <div
                            className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                              active
                                ? "bg-[#D4AF37] border-[#D4AF37] text-black"
                                : "bg-black border-white/15 text-white/40"
                            } ${isCurrent ? "ring-4 ring-[#D4AF37]/25" : ""}`}
                          >
                            <s.Icon className="w-4 h-4" />
                          </div>
                          <div className={`mt-3 font-mono-space text-[11px] uppercase tracking-wider ${active ? "text-white" : "text-white/40"}`}>
                            {s.label}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-8 flex items-start gap-3 p-4 border border-white/10 rounded-2xl text-sm text-white/70">
                  <Clock className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                  <div>
                    {STEPS[currentIndex >= 0 ? currentIndex : 0].hint}
                    <div className="text-xs text-white/40 mt-1">
                      Courier updates are performed by the Teuralink admin as the deed moves through preparation, dispatch, and delivery.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-xs text-white/45 text-center">
              This tracking page reflects the physical shipment of your commemorative printed deed. It does not provide a downloadable deed or represent legal land ownership.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Item({ k, v, mono }) {
  return (
    <div>
      <div className="overline">{k}</div>
      <div className={`mt-1 ${mono ? "font-mono-space text-sm" : "text-base"} text-white`}>{v}</div>
    </div>
  );
}

function Row({ Icon, k, v, testid }) {
  return (
    <div className="flex items-start gap-3 p-4 bg-white/[0.02] border border-white/10 rounded-2xl">
      <Icon className="w-5 h-5 text-gold mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="overline">{k}</div>
        <div className="mt-1 text-white truncate" data-testid={testid}>{v}</div>
      </div>
    </div>
  );
}
